import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Search, Plus, Info } from "lucide-react";
import {
  nutritionAPI,
  type ServingUnitPayload,
  type ServingUnitResponse,
} from "@/lib/nutrition-api";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { getApiErrorMessage, getApiErrorStatus } from "@/lib/api";
import { AccessDeniedCard, RequiredMark } from "./nutrition-shared";

const servingUnitSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name must be 100 characters or less"),
});

type ServingUnitFormValues = z.infer<typeof servingUnitSchema>;

type DialogMode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; code: number };

export const ViewServingUnits = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogMode, setDialogMode] = useState<DialogMode>({ kind: "closed" });

  const {
    data: servingUnits,
    isLoading,
    error: servingUnitsError,
  } = useQuery({
    queryKey: queryKeys.nutrition.servingUnits.list({
      search: searchQuery || undefined,
      ordering: "name",
    }),
    queryFn: () =>
      nutritionAPI.servingUnits.list({
        search: searchQuery || undefined,
        ordering: "name",
      }),
    retry: false,
  });

  useEffect(() => {
    if (servingUnitsError) {
      toast({
        title: "Error",
        description:
          (servingUnitsError as any)?.message ||
          "Failed to fetch serving units",
        variant: "destructive",
      });
    }
  }, [servingUnitsError, toast]);

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_NUTRITION_DB)) {
    return <AccessDeniedCard feature="Serving Units" />;
  }

  const list = servingUnits || [];
  const totalCount = list.length;
  const editingUnit =
    dialogMode.kind === "edit"
      ? (list.find((u) => u.code === dialogMode.code) ?? null)
      : null;

  return (
    <div className="h-full flex flex-col gap-4">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="z-h2">Serving Units</CardTitle>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setDialogMode({ kind: "create" })}
          >
            <Plus className="h-4 w-4" />
            Add Serving Unit
          </Button>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search serving units by name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Search"
              )}
            </Button>
          </div>

          <div className="mb-3 z-caption text-muted-foreground">
            Showing {totalCount} serving unit{totalCount !== 1 ? "s" : ""}
            {searchQuery && ` for "${searchQuery}"`}
          </div>

          <div className="border rounded-lg flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[100px] z-eyebrow text-muted-foreground">
                    Code
                  </TableHead>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Name
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      <p className="z-caption text-muted-foreground mt-2">
                        Loading serving units...
                      </p>
                    </TableCell>
                  </TableRow>
                ) : list.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {searchQuery
                        ? "No serving units match your search."
                        : "No serving units found. Add one to get started."}
                    </TableCell>
                  </TableRow>
                ) : (
                  list.map((item) => (
                    <TableRow
                      key={item.code}
                      className="cursor-pointer transition-colors hover:bg-muted/30"
                      onClick={() =>
                        setDialogMode({ kind: "edit", code: item.code })
                      }
                    >
                      <TableCell className="z-body">{item.code}</TableCell>
                      <TableCell className="z-body">{item.name}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ServingUnitFormDialog
        mode={dialogMode}
        unit={editingUnit}
        onOpenChange={(open) => {
          if (!open) setDialogMode({ kind: "closed" });
        }}
        onSuccess={() => {
          queryClient.invalidateQueries(
            invalidateQueries.nutritionServingUnits()
          );
          setDialogMode({ kind: "closed" });
        }}
      />
    </div>
  );
};

interface ServingUnitFormDialogProps {
  mode: DialogMode;
  unit: ServingUnitResponse | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const ServingUnitFormDialog = ({
  mode,
  unit,
  onOpenChange,
  onSuccess,
}: ServingUnitFormDialogProps) => {
  const { toast } = useToast();
  const isOpen = mode.kind !== "closed";
  const isCreating = mode.kind === "create";

  const form = useForm<ServingUnitFormValues>({
    resolver: zodResolver(servingUnitSchema),
    defaultValues: { name: "" },
    mode: "onSubmit",
  });

  useEffect(() => {
    // Only the create form is editable — reset it each time it opens. The edit
    // path is read-only (names are immutable), so it needs no form state.
    if (isOpen && isCreating) {
      form.reset({ name: "" });
    }
  }, [isOpen, isCreating, form]);

  const createMutation = useMutation({
    mutationFn: (data: ServingUnitPayload) =>
      nutritionAPI.servingUnits.create(data),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Serving unit created successfully!",
      });
      onSuccess();
    },
    onError: (error: unknown) => {
      const isPermissionError = getApiErrorStatus(error) === 403;
      toast({
        title: isPermissionError ? "Access Denied" : "Error",
        description: isPermissionError
          ? "Your permissions no longer allow nutrition database changes."
          : getApiErrorMessage(error, "Failed to create serving unit"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: ServingUnitFormValues) => {
    createMutation.mutate({ name: values.name });
  };

  const isPending = createMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isCreating ? "Add Serving Unit" : "Serving Unit"}
          </DialogTitle>
        </DialogHeader>

        {isCreating ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Name <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Cup, Gram, Milliliter"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  )}
                  Create
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          // Names are immutable since FV6 — a rename would silently relabel this
          // unit on every historical plan that pinned it, so the PATCH now 400s.
          // Renaming is retired; the unit is shown read-only.
          <div className="space-y-4">
            {unit && (
              <>
                <div className="space-y-1">
                  <div className="z-label text-muted-foreground">Code</div>
                  <div className="z-body">{unit.code}</div>
                </div>
                <div className="space-y-1">
                  <div className="z-label text-muted-foreground">Name</div>
                  <div className="z-body">{unit.name}</div>
                </div>
              </>
            )}
            <Alert>
              <Info className="text-warning" />
              <AlertTitle>Serving unit names can't be changed</AlertTitle>
              <AlertDescription>
                Renaming a unit would relabel it on every historical plan that
                used it. To use a different name, add a new serving unit
                instead.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewServingUnits;
